import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleIcon from "@mui/icons-material/People";
import MessageIcon from "@mui/icons-material/Message";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DraftsIcon from "@mui/icons-material/Drafts";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "#64748b", bg: "#f1f5f9", icon: <DraftsIcon sx={{ fontSize: 14 }} /> },
  scheduled: { label: "Scheduled", color: "#d97706", bg: "#fffbeb", icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
  sending: { label: "Sending", color: "#2563eb", bg: "#eff6ff", icon: <SendIcon sx={{ fontSize: 14 }} /> },
  completed: { label: "Completed", color: "#16a34a", bg: "#f0fdf4", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  failed: { label: "Failed", color: "#dc2626", bg: "#fef2f2", icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
};

export default function CampaignCard({ campaign, onClick, onEdit, onDelete }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const status = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const stats = campaign.stats || { sent: 0, failed: 0, total: 0 };
  const progress = stats.total > 0 ? (stats.sent / stats.total) * 100 : 0;
  const isActive = campaign.status === "completed" || campaign.status === "sending";

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onEdit(campaign._id);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onDelete(campaign._id);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        borderTop: `3px solid ${theme.palette.primary.main}`,
        bgcolor: "#fff",
        transition: "all 0.2s ease-in-out",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 8px 20px -8px rgba(0, 0, 0, 0.1)",
          borderColor: theme.palette.primary.light,
          borderTopColor: theme.palette.primary.main,
        },
      }}
    >
      {/* Card Body */}
      <Box sx={{ p: 2, flex: 1 }}>
        {/* Name + Menu */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.3 }} noWrap>
            {campaign.name}
          </Typography>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              ml: 0.5,
              mt: -0.5,
              color: "text.disabled",
              "&:hover": { color: "text.primary", bgcolor: "action.hover" },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {/* Status + Tag Chips */}
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
          <Chip
            icon={status.icon}
            label={status.label}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              color: status.color,
              bgcolor: status.bg,
              border: `1px solid ${status.color}30`,
              "& .MuiChip-icon": { color: status.color, fontSize: 13, ml: 0.5 },
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
          {campaign.tag && (
            <Chip
              label={campaign.tag}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                color: "text.secondary",
                bgcolor: "action.hover",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}
          {campaign.isReusable && (
            <Tooltip title="Reusable Campaign">
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: theme.palette.primary.main,
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* Template Info */}
        <Box
          sx={{
            p: 1.25,
            bgcolor: "background.default",
            borderRadius: 2,
            mb: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            >
              <MessageIcon sx={{ fontSize: 15 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10, color: "text.disabled", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Template
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }} noWrap>
                {campaign.template?.name || "No template"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Contacts & Progress */}
        {isActive ? (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PeopleIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>
                  {stats.total} {stats.total === 1 ? "Contact" : "Contacts"}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.palette.primary.main }}>
                {Math.round(progress)}% Sent
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 5,
                borderRadius: 3,
                bgcolor: "action.hover",
                "& .MuiLinearProgress-bar": {
                  bgcolor: theme.palette.primary.main,
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        ) : (
          <Stack direction="row" spacing={3}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                <PeopleIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                <Typography sx={{ fontSize: 10, color: "text.disabled", fontWeight: 500 }}>
                  Contacts
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
                {stats.total} {stats.total === 1 ? "Contact" : "Contacts"}
              </Typography>
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                <AccessTimeIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                <Typography sx={{ fontSize: 10, color: "text.disabled", fontWeight: 500 }}>
                  Sent Date
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
                {formatDate(campaign.sentAt || campaign.createdAt)}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Sent / Failed Stats */}
        {(isActive || campaign.status === "failed") && (
          <Stack direction="row" spacing={3} sx={{ mt: 1.25 }}>
            <Box>
              <Typography sx={{ fontSize: 10, color: "text.disabled", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Sent
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "success.main" }}>
                {stats.sent}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10, color: "text.disabled", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Failed
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "error.main" }}>
                {stats.failed}
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          component="button"
          onClick={onClick}
          sx={{
            border: "none",
            bgcolor: "transparent",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            py: 0.5,
            px: 2,
            borderRadius: 1.5,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: "action.hover",
              color: "text.primary",
            },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} />
          View Details
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 2,
          sx: {
            borderRadius: 2,
            minWidth: 140,
            mt: 1,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          },
        }}
      >
        <MenuItem onClick={handleEditClick} sx={{ fontSize: 13, gap: 1.5, py: 1 }}>
          <EditIcon sx={{ fontSize: 16, color: "text.secondary" }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ fontSize: 13, gap: 1.5, py: 1, color: "error.main" }}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} /> Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
}
